import { computed, onMounted, onUnmounted, ref } from 'vue'

import { useRoute } from 'nuxt/app'

import { isCPAuthRoutePath } from '@clientportal/routing-layer/utils/auth-route.utils'

/** Matches CPRootContainer scoped CSS `@media (max-width: 767px)` (mobile shell: drawer + hide persistent sidebar). */
const CP_SHELL_MOBILE_MAX_WIDTH = 768

/**
 * Centralized route and viewport context for CPRootContainer.
 * Provides route-derived state, auth/page checks, and visibility flags.
 */
export function useCPRouteContext() {
  const route = useRoute()
  const isMobile = ref(false)

  const checkMobile = () => {
    isMobile.value = typeof window !== 'undefined' && window.innerWidth < CP_SHELL_MOBILE_MAX_WIDTH
  }

  const isAuthPage = computed((): boolean => {
    return isCPAuthRoutePath(route?.path)
  })

  const isAccountSettingsPage = computed((): boolean => {
    if (!route?.path) return false
    const path = route.path
    return path === '/account' || path.startsWith('/account/') || !!route.name?.toString().includes('account-settings')
  })

  /** True when no matched record is marked as a clientportal shell page (see routing-layer pages:extend cpRoute). */
  const isNonCPNavigationRoute = computed((): boolean => {
    const matched = route.matched
    if (!matched?.length) {
      return true
    }
    return !matched.some(record => record.meta?.cpRoute === true)
  })

  /** True when on a communities-layer route (groups/users/conversations), not the CP shell communities pages. */
  const isCommunityLayerPage = computed((): boolean => {
    if (!route?.path) return false
    return route.path.startsWith('/communities') && !route.matched.some(r => r.meta?.cpRoute === true)
  })

  const currentGroupSlug = computed(() => {
    if (route.path.includes('/communities/groups/') || route.path.includes('/groups/')) {
      return (route.params.groupSlug as string) ?? null
    }
    if (route.params.groupSlug) {
      return route.params.groupSlug as string
    }
    return null
  })

  const selectedProductId = computed(() => (route.params.productId as string) || '')

  /**
   * Narrow SwitcherSidebar (courses + communities rail) for embedded learning under a group,
   * e.g. `/communities/groups/:groupSlug/learning/...` or GoKollab `/:groupSlug/learning/...`.
   */
  const shouldUseSwitcherSidebar = computed((): boolean => /\/learning(\/|$)/.test(route.path))

  onMounted(() => {
    if (typeof window !== 'undefined') {
      checkMobile()
      window.addEventListener('resize', checkMobile)
    }
  })

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', checkMobile)
    }
  })

  return {
    route,
    isMobile,
    isAuthPage,
    isAccountSettingsPage,
    isNonCPNavigationRoute,
    isCommunityLayerPage,
    currentGroupSlug,
    selectedProductId,
    shouldUseSwitcherSidebar,
  }
}
